import { tokenCreate, createDeviceUser } from '$lib/stores/user/action';
import { user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import { AOFL_PRODUCT_UUIDS, OUTBOUND_PATH, PAGES } from '$lib/utils/constants';
import { getDeviceId } from '$lib/utils/device-id';
import { isMobileViewer, startLoading, stopLoading } from '$lib/stores/app/actions';
import { Sentry } from '../sentry';
import { AnalyticsService } from '$lib/services/analytics';

const WEB_APP_CREATE_CHILD_ACCOUNT_PATH = '/onboarding/create-child-account';

/**
 * FlutterAppService handles navigation to the Flutter 2.0 web application.
 *
 * This service manages authentication tokens and provides a simple interface
 * for navigating from Svelte pages to the Flutter app with proper authentication.
 */
class FlutterAppService {
	static async navigate() {
		startLoading();
		try {
			if (isMobileViewer()) {
				await this.navigateToNativeApp();
			} else {
				await this.navigateToWebApp();
			}
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Navigate to Flutter App'
				}
			});
		} finally {
			stopLoading();
		}
	}

	/**
	 * Ensures we have a valid token, creating one if needed.
	 * @returns {Promise<string>} The token to use
	 * @throws {Error} When unable to create or retrieve a valid token
	 */
	static async ensureToken() {
		// If we have a valid session, create a short-lived token
		if (get(user).tokenValid) {
			const response = await tokenCreate(AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0);
			return response.data?.token || '';
		}

		// If no valid session, create device session, and then create a short-lived token
		await createDeviceUser();
		if (get(user).tokenValid) {
			const response = await tokenCreate(AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0);
			return response.data?.token || '';
		}

		throw new Error('Unable to obtain authentication token');
	}

	static async navigateToNativeApp() {
		const token = await this.ensureToken();
		const appMeta = get(app).meta;
		let url;
		if (appMeta.isAmazonViewer) {
			url = OUTBOUND_PATH.LINK_TO_APP_2_0_AMAZON;
		} else {
			url = OUTBOUND_PATH.LINK_TO_APP_2_0;
		}
		await AnalyticsService.trackClientDebug({
			eventSubType: 'mkt-exit-event',
			source: 'FlutterAppService: navigateToNativeApp',
			destination: `${url}${token}`
		});
		window.location.href = `${url}${token}`;
	}

	/**
	 * Builds the web app URL with the provided token
	 * @param {string} providedToken - The authentication token
	 * @returns {Promise<string>} The web app URL
	 */
	static async buildWebAppUrl(providedToken = '') {
		const token = providedToken || (await this.ensureToken());
		const deviceId = getDeviceId();
		const params = new URLSearchParams();
		params.append('device_id', deviceId);
		params.append('redirect_path', WEB_APP_CREATE_CHILD_ACCOUNT_PATH);
		return `${PAGES.EXTERNAL.ABCMOUSE_2_0}/auth/${token}?${params.toString()}`;
	}

	/**
	 * Navigates to the Flutter web app with authentication token
	 * @param {string} providedToken - The authentication token
	 * @returns {Promise<void>}
	 */
	static async navigateToWebApp(providedToken = '') {
		const url = await this.buildWebAppUrl(providedToken);
		await AnalyticsService.trackClientDebug({
			eventSubType: 'mkt-exit-event',
			source: 'FlutterAppService: navigateToWebApp',
			destination: url
		});
		window.location.href = url;
	}
}

export { FlutterAppService };
